"use client";

import { useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination, A11y, EffectFade } from "swiper/modules";
import type { Swiper as SwiperClass } from "swiper/types";
import type { WPrezentationContent, WPrezentationSlide } from "@/lib/api/cms-window-content";
import { useT } from "@/lib/i18n/context";
import { wPrezentationTranslations, type WPrezentationTranslations } from "./WPrezentation.i18n";
import styles from "./WPrezentation.module.css";

/* Swiper CSS — importujemy core + tylko te moduły których faktycznie używamy
   (mniejszy bundle niż swiper-bundle.css). */
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/autoplay";
import "swiper/css/effect-fade";

/**
 * WPrezentation — hero slider (typ okna `w_prezentation`).
 *
 * **Parytet z legacy** `template/default_modern/.../w_prezentation_default_modern.imp`:
 *   - Swiper.js z autoplay / pagination / navigation per config
 *   - 3-wariantowe responsive obrazy (`desktop` / `tablet` / `mobile`) renderowane
 *     przez natywny `<picture>` + `<source media>` (zamiast legacy JS resize-swap)
 *   - 3 typy slajdów: image / video / YouTube embed
 *   - Per-slajd: background_color, text align (L/C/R), theme (light/dark), CTA href
 *   - Slider wysokość jako % viewportu (`height_div_to_display` vh)
 *
 * **Różnice vs legacy:**
 *   - Responsive images: natywny `<picture>` zamiast `changeBgImg()` na window.resize
 *     — SEO-friendly, lazy load OOTB, brak hydration mismatch'a
 *   - YouTube: `iframe` z `loading="lazy"` + `srcdoc` fallback (legacy ładuje od razu)
 *   - Accessibility: Swiper `A11y` module — ARIA labels, keyboard navigation
 *
 * Dispatcher (`WindowRenderer`) wstrzykuje `content` z `WindowContentResponse.data.response_window_data`.
 */
export default function WPrezentation({ content }: WPrezentationProps) {
  const t = useT(wPrezentationTranslations);

  if (!content || content.slides.length === 0) {
    return (
      <div className={styles.empty} role="status">
        {t.empty}
      </div>
    );
  }

  const { config, slides } = content;

  /* Swiper config — 1:1 mapping z legacy template. autoplay tylko gdy włączony,
     loop zawsze (jak legacy), pauseOnMouseEnter dla autoplay. */
  const swiperModules = [Navigation, Pagination, Autoplay, A11y, EffectFade];
  const autoplayEnabled = config.slideshow && slides.length > 1;
  const autoplay = autoplayEnabled
    ? { delay: config.slideshow_freq * 1000, pauseOnMouseEnter: true, disableOnInteraction: false }
    : false;

  /* Progress bar dla autoplay — fill pokazuje "ile zostało do następnego slajdu".
     Update przez ref żeby uniknąć re-renderu React'a co frame (Swiper firuje
     `autoplayTimeLeft` ~60 razy/s — przez state byłoby kosztowne).
     `percentage`: 1 = start cyklu (po switch), 0 = przed switch.
     Fill rośnie od 0 do 100% (visual: pasek "ładuje się"). */
  const progressFillRef = useRef<HTMLDivElement | null>(null);
  const handleAutoplayTimeLeft = (
    _swiper: SwiperClass,
    _timeLeft: number,
    percentage: number,
  ) => {
    if (progressFillRef.current) {
      progressFillRef.current.style.transform = `scaleX(${1 - percentage})`;
    }
  };

  /* Custom navigation buttons — Swiper default DOM (`<div class="swiper-button-prev">`)
     używa pseudo-elementu `::after` + font `swiper-icons` z `content: 'prev'/'next'`
     który czasem widać jako duplikat strzałki. Renderujemy własne `<button>` z inline
     SVG i podajemy refy do `<Swiper navigation>` — Swiper przejmuje click handlers
     + disable state. `useState` zamiast `useRef` na elementy bo Swiper potrzebuje
     stable DOM ref po mount (state wymusza re-render gdy ref'y są gotowe). */
  const [prevEl, setPrevEl] = useState<HTMLButtonElement | null>(null);
  const [nextEl, setNextEl] = useState<HTMLButtonElement | null>(null);

  return (
    <div
      className={styles.root}
      aria-roledescription="carousel"
      aria-label={content.title}
    >
      <Swiper
        modules={swiperModules}
        slidesPerView={1}
        loop={slides.length > 1}
        speed={config.slideshow_speed}
        autoplay={autoplay}
        pagination={config.show_pagination ? { clickable: true } : false}
        navigation={
          config.show_navigation
            ? { prevEl, nextEl }
            : false
        }
        a11y={{ enabled: true }}
        /* Fade transition zamiast horizontal slide — slajdy faduje in/out z
           crossFade (nowy slajd staje się widoczny w tym samym czasie gdy stary
           znika; bez `crossFade: true` byłby czarny flash pomiędzy). Wymaga
           modułu `EffectFade` + `swiper/css/effect-fade`. */
        effect="fade"
        fadeEffect={{ crossFade: true }}
        onAutoplayTimeLeft={autoplayEnabled ? handleAutoplayTimeLeft : undefined}
      >
        {slides.map((slide, index) => (
          <SwiperSlide key={slide.id}>
            <SlideContent slide={slide} showOverlay={config.show_title} priority={index === 0} t={t} />
          </SwiperSlide>
        ))}
      </Swiper>

      {config.show_navigation && slides.length > 1 && (
        <>
          <button
            ref={setPrevEl}
            type="button"
            className={`${styles.navBtn} ${styles.navPrev}`}
            aria-label={t.prevSlide}
          >
            <ChevronIcon direction="left" />
          </button>
          <button
            ref={setNextEl}
            type="button"
            className={`${styles.navBtn} ${styles.navNext}`}
            aria-label={t.nextSlide}
          >
            <ChevronIcon direction="right" />
          </button>
        </>
      )}

      {autoplayEnabled && (
        <div className={styles.progress} aria-hidden="true">
          <div ref={progressFillRef} className={styles.progressFill} />
        </div>
      )}
    </div>
  );
}

/* ─── Chevron icon ────────────────────────────────────────────────── */

function ChevronIcon({ direction }: { direction: "left" | "right" }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {direction === "left" ? (
        <polyline points="15 18 9 12 15 6" />
      ) : (
        <polyline points="9 18 15 12 9 6" />
      )}
    </svg>
  );
}

export type WPrezentationProps = {
  content: WPrezentationContent | null;
};

/* ─── Slide renderer ──────────────────────────────────────────────── */

function SlideContent({
  slide,
  showOverlay,
  priority,
  t,
}: {
  slide: WPrezentationSlide;
  showOverlay: boolean;
  /** Pierwszy slajd = kandydat LCP — eager load + fetchpriority=high. */
  priority: boolean;
  t: WPrezentationTranslations;
}) {
  const bgStyle = slide.background_color
    ? { backgroundColor: `#${slide.background_color}` }
    : undefined;

  /* Overlay z tytułem/subtytułem/CTA — renderujemy tylko gdy `show_title=true`
     na poziomie okna ORAZ mamy chociaż jedno pole (name/subname/href).
     Legacy template ma identyczną logikę. */
  const hasOverlayContent = Boolean(slide.name || slide.subname || slide.href);
  const renderOverlay = showOverlay && hasOverlayContent;

  return (
    <div className={styles.slide} style={bgStyle}>
      <div className={styles.media}>
        <SlideMedia slide={slide} priority={priority} t={t} />
      </div>

      {/* Slajd-as-link — owijamy <a> nad media ale POD overlay'em (z-index).
          Brak href: nie renderujemy linka, overlay jest nieklikalny.
          Z href: kliknięcie GDZIEKOLWIEK na slajdzie nawiguje, ALE CTA wewnątrz
          overlay ma własny z-index żeby przechwycić kliknięcie. */}
      {slide.href && (
        <a
          href={slide.href}
          className={styles.slideLink}
          aria-label={slide.name ?? t.openSlideAria}
        />
      )}

      {renderOverlay && (
        <div className={styles.overlay}>
          {slide.name && (
            <h2
              className={styles.title}
              // Backend dopuszcza inline HTML (h1/h2/h3/p/span/br) w name.
              // Trust source: content tylko z IADMIN, sanitized przez IFILTER::string.
              dangerouslySetInnerHTML={{ __html: slide.name }}
            />
          )}
          {slide.subname && (
            <p
              className={styles.subtitle}
              dangerouslySetInnerHTML={{ __html: slide.subname }}
            />
          )}
          {slide.href && (
            <a href={slide.href} className={styles.cta}>
              {t.cta}
            </a>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Media renderer per slide.type ───────────────────────────────── */

function SlideMedia({ slide, priority, t }: { slide: WPrezentationSlide; priority: boolean; t: WPrezentationTranslations }) {
  /* type=1 — natywny <picture> z 3 wariantami responsive. Browser sam
     wybiera wariant per viewport bez JS. Mobile/tablet są opcjonalne —
     gdy null, browser użyje desktop (fallback <img src>).
     `priority` (pierwszy slajd, kandydat LCP): eager load + fetchPriority=high
     + decoding=async. Pozostałe slajdy zostają lazy (Swiper trzyma wszystkie
     w DOM, ale niewidoczne — browser opóźnia ich download). */
  if (slide.type === 1 && slide.image) {
    const { desktop, tablet, mobile } = slide.image;
    if (!desktop) return null;

    return (
      <picture>
        {mobile && <source media="(max-width: 767px)" srcSet={mobile} />}
        {tablet && <source media="(max-width: 1024px)" srcSet={tablet} />}
        <img
          src={desktop}
          alt={slide.name ?? ""}
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : "auto"}
          decoding="async"
        />
      </picture>
    );
  }

  /* type=2 — video plik. Autoplay muted (jedyny sposób na autoplay w nowoczesnych
     browserach), loop, playsInline (iOS). Dla pierwszego slajdu `preload="auto"`
     żeby browser zaczął ładować od razu (LCP); pozostałe `metadata` (oszczędność). */
  if (slide.type === 2 && slide.video) {
    return (
      <video autoPlay loop muted playsInline preload={priority ? "auto" : "metadata"}>
        <source src={slide.video.file_url} />
      </video>
    );
  }

  /* type=3 — YouTube embed. Lazy iframe — ładuje się gdy slajd staje się
     widoczny (Swiper rendreuje wszystkie ale lazy="loading" w browser opóźnia). */
  if (slide.type === 3 && slide.youtube) {
    const ytId = slide.youtube.video_id;
    const src = `https://www.youtube.com/embed/${ytId}?rel=0&modestbranding=1&controls=0&showinfo=0&autoplay=0&loop=1&mute=1&playlist=${ytId}`;
    return (
      <iframe
        src={src}
        title={slide.name ?? t.youtubeTitleFallback}
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }

  return null;
}
